import { useState, useEffect, useRef, useCallback } from 'react';
import { toaster } from 'evergreen-ui';
import DiaryService from '../../services/diaryService';
import { useAuth } from '../../services/authContext';

const SLOT_HEIGHT = 48;
const DAY_HOURS = 24;
const DAY_HEIGHT = SLOT_HEIGHT * DAY_HOURS;

export { SLOT_HEIGHT, DAY_HOURS, DAY_HEIGHT };

const useDiary = ({ guests = [], diaryEntries: propEntries, setDiaryEntries, isDirector, isEducator }) => {
  const [entries, setEntries] = useState([]);
  const [futureAppointments, setFutureAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const { getUserId } = useAuth();
  const userId = getUserId && getUserId();
  const containerRef = useRef(null);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDescriptionDialog, setShowDescriptionDialog] = useState(false);
  const [showEditDescriptionDialog, setShowEditDescriptionDialog] = useState(false);
  const [showEditNoteDialog, setShowEditNoteDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [form, setForm] = useState({
    testo: '',
    guest_id: '',
    start_time: '',
    end_time: '',
    entry_type: 'appuntamento',
    notes: [],
  });
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  const fetchEntries = useCallback(async (date, forceFetch = false) => {
    try {
      const dayKey = (d) => {
        const _ = new Date(d);
        const yyyy = _.getFullYear();
        const mm = String(_.getMonth() + 1).padStart(2, '0');
        const dd = String(_.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      const entryMatchesDate = (entry, dateObj) => {
        if (!dateObj) return true;
        const day = dayKey(dateObj);
        if (!entry) return false;
        if (entry.entry_type === 'appuntamento') {
          if (!entry.start_time) return false;
          try { return dayKey(entry.start_time) === day; } catch (e) { return false; }
        }
        try { return dayKey(entry.timestamp || entry.date || entry.start_time) === day; } catch (e) { return false; }
      };

      if (!forceFetch && propEntries !== undefined && propEntries !== null) {
        const filtered = (propEntries || []).filter(en => entryMatchesDate(en, date));
        setEntries(filtered);
        return filtered;
      }
      if (date) {
        const data = await DiaryService.getEntriesByDate(date);
        setEntries(data || []);
        return data || [];
      } else {
        const data = await DiaryService.getAllEntries();
        setEntries(data || []);
        return data || [];
      }
    } catch (error) {
      console.error('Errore durante il recupero delle descrizioni:', error);
      return [];
    }
  }, [propEntries]);

  useEffect(() => {
    (async () => {
      if (propEntries !== undefined && propEntries !== null) {
        await fetchEntries(selectedDate, true);
        return;
      }
      await fetchEntries(selectedDate);
    })();
  }, [selectedDate, propEntries, fetchEntries]);

  const fetchFutureAppointments = async () => {
    try {
      const all = await DiaryService.getAllEntries();
      if (!Array.isArray(all)) { setFutureAppointments([]); return []; }
      const now = new Date();
      const future = (all || []).filter(
        en => en && en.entry_type === 'appuntamento' && en.start_time && (new Date(en.start_time).getTime() > now.getTime())
      );
      setFutureAppointments(future);
      return future;
    } catch (err) {
      console.error('Errore fetchFutureAppointments', err);
      setFutureAppointments([]);
      return [];
    }
  };

  useEffect(() => {
    fetchFutureAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onEventClick = (ev) => {
    setEditingEntry(ev);
    setForm({
      testo: ev.type === 'entry' ? (ev.data.testo || '') : (ev.parent && ev.parent.testo ? ev.parent.testo : ''),
      guest_id: ev.data && ev.data.guest_id
        ? String(ev.data.guest_id)
        : (ev.parent && ev.parent.guest_id ? String(ev.parent.guest_id) : ''),
      start_time: ev.start ? ev.start.toISOString() : '',
      end_time: ev.end ? ev.end.toISOString() : '',
      entry_type: ev.type === 'entry' ? 'appuntamento' : 'descrizione',
      notes: ev.type === 'note' ? [ev.data] : (ev.parent && ev.parent.notes ? ev.parent.notes : []),
    });

    if (ev.type === 'entry' && (isDirector || isEducator)) {
      setShowAddDialog(true);
    } else if (ev.type === 'note') {
      if (isDirector) {
        setEditingNote(ev);
        setShowEditNoteDialog(true);
      } else {
        setShowViewDialog(true);
      }
    } else {
      setShowViewDialog(true);
    }
  };

  const onEmptyDoubleClick = (e) => {
    const rect = containerRef.current && containerRef.current.getBoundingClientRect();
    if (!rect) return;
    const y = e.clientY - rect.top;
    const minutes = Math.round((y / DAY_HEIGHT) * 24 * 60);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const sel = selectedDate ? new Date(selectedDate) : new Date();
    const dt = new Date(sel.getFullYear(), sel.getMonth(), sel.getDate(), hours, mins, 0, 0);

    setForm({
      testo: '',
      guest_id: '',
      start_time: dt.toISOString(),
      end_time: new Date(dt.getTime() + 30 * 60000).toISOString(),
      entry_type: (dt > new Date() ? 'appuntamento' : 'descrizione'),
      notes: [],
    });
    setEditingEntry({
      start: dt.toISOString(),
      end: new Date(dt.getTime() + 30 * 60000).toISOString(),
      entry_type: (dt > new Date() ? 'appuntamento' : 'descrizione'),
    });
    if (dt > new Date()) {
      setShowAddDialog(true);
    } else {
      setShowDescriptionDialog(true);
    }
  };

  const handleAddSubmit = async (payloadOverride) => {
    try {
      const { toLocalSQLDatetime } = await import('../../utils/timeUtils');
      const payload = payloadOverride ? payloadOverride : {
        user_id: userId,
        testo: form.testo,
        date: toLocalSQLDatetime(form.start_time) || new Date().toISOString(),
        start_time: form.start_time ? toLocalSQLDatetime(form.start_time) : undefined,
        end_time: form.end_time ? toLocalSQLDatetime(form.end_time) : undefined,
        guest_id: form.guest_id === '' ? null : form.guest_id,
        entry_type: form.entry_type || 'appuntamento',
        notes: form.notes && form.notes.length ? form.notes : undefined,
      };
      const isUpdate = !!payload.id;
      if (isUpdate) {
        await DiaryService.updateEntry(payload);
      } else {
        await DiaryService.addEntry(payload);
      }
      toaster.success(isUpdate ? 'Voce aggiornata' : 'Voce aggiunta con successo');
      setShowAddDialog(false);
      setShowDescriptionDialog(false);
      const fresh = await fetchEntries(selectedDate, true);
      if (setDiaryEntries) setDiaryEntries(fresh || []);
      await fetchFutureAppointments();
    } catch (err) {
      console.error(err);
      toaster.danger('Errore durante il salvataggio');
    }
  };

  const handleDelete = async (id) => {
    try {
      await DiaryService.deleteEntry(id);
      toaster.success('Voce eliminata');
      setShowAddDialog(false);
      const fresh = await fetchEntries(selectedDate, true);
      if (setDiaryEntries) setDiaryEntries(fresh || []);
      await fetchFutureAppointments();
    } catch (err) {
      console.error(err);
      toaster.danger("Errore durante l'eliminazione");
    }
  };

  return {
    // state
    entries,
    futureAppointments,
    selectedDate,
    setSelectedDate,
    userId,
    containerRef,
    isMobile,
    form,
    editingEntry,
    setEditingEntry,
    editingNote,
    setEditingNote,
    // dialog visibility
    showAddDialog,
    setShowAddDialog,
    showViewDialog,
    setShowViewDialog,
    showDescriptionDialog,
    setShowDescriptionDialog,
    showEditDescriptionDialog,
    setShowEditDescriptionDialog,
    showEditNoteDialog,
    setShowEditNoteDialog,
    // handlers
    fetchEntries,
    onEventClick,
    onEmptyDoubleClick,
    handleAddSubmit,
    handleDelete,
  };
};

export default useDiary;
